import { JsonSchema4, JsonSchema7 } from "@jsonforms/core";
import { GapReferenceKeys, GapReferenceType } from "./types";
import { Stack, Text } from "@certn/cascadia";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  PhoneNumberInput,
  Select,
  SelectItem,
  TextField,
} from "@certn/cascadia-react";
import { useContext } from "react";
import { CascadiaJsonFormContext } from "@certn/forms";

type GapReferenceProps = {
  index: number;
  gap_reference: Partial<GapReferenceType>;
  schema: JsonSchema4 | JsonSchema7;
  onGapReferenceChange: (gap_reference: Partial<GapReferenceType>) => void;
};

export function GapReference({
  index,
  gap_reference,
  schema,
  onGapReferenceChange,
}: GapReferenceProps) {
  const { t } = useTranslation();
  const { submitErrors } = useContext(CascadiaJsonFormContext);

  const KEY_TO_LABEL: Record<string, string> = {
    referee_name: t("Referee name"),
    email: t("Referee email"),
    relationship: t("Relationship to referee"),
    relationship_length: t("Years known"),
    phone_number: t("Referee phone number"),
    job_title: t("Referee job title"),
  };

  return (
    <Stack testID={`gap-reference-${index}`}>
      <Text
        testID={`gap-reference-heading-${index}`}
        style="form/section-heading"
      >
        {t("Reference for this gap")}
      </Text>
      <Stack gap="space-3">
        {Object.entries(schema).map(([name, property]) => {
          const key = `key-gap-reference-${name}-gap-${index}`;
          const testID = `gap-reference-${name}-gap-${index}`;
          const errors =
            submitErrors[`gap_history.gaps.${index}.gap_reference.${name}`] ??
            [];
          const derivedValue = gap_reference[name as GapReferenceKeys];
          const value = derivedValue ? derivedValue.toString() : undefined;

          if (property.oneOf)
            return (
              <Select
                key={key}
                testID={testID}
                isRequired
                label={KEY_TO_LABEL[name]}
                errors={errors}
                value={value}
                onChange={(key) => {
                  if (key) {
                    onGapReferenceChange({
                      ...gap_reference,
                      [name]: key,
                    });
                  }
                }}
              >
                {property.oneOf.map(
                  (option: { const: string; title: string }) => (
                    <SelectItem
                      key={option.const}
                      id={option.const}
                      textValue={option.title}
                    >
                      {option.title}
                    </SelectItem>
                  )
                )}
              </Select>
            );

          if (property.type === "boolean")
            return (
              <Checkbox
                key={key}
                testID={testID}
                isRequired
                errors={errors}
                onChange={(isSelected) => {
                  onGapReferenceChange({
                    ...gap_reference,
                    [name]: isSelected,
                  });
                }}
                defaultSelected={property.default}
                isSelected={!!derivedValue}
              >
                {property.description}
              </Checkbox>
            );

          if (property.format === "phone_number")
            return (
              <PhoneNumberInput
                key={key}
                testID={testID}
                name="phone_number"
                label={KEY_TO_LABEL[name]}
                type="string"
                isRequired
                value={value}
                onChange={(value) => {
                  onGapReferenceChange({
                    ...gap_reference,
                    [name]: value,
                  });
                }}
              />
            );

          return (
            <TextField
              key={key}
              testID={testID}
              isRequired
              label={KEY_TO_LABEL[name]}
              type={property.format === "email" ? "email" : "text"}
              value={value}
              onChange={(value) => {
                onGapReferenceChange({
                  ...gap_reference,
                  [name]: value.trim() === "" ? null : value,
                });
              }}
              description={property.description}
              errors={errors}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
